<template>
	<v-dialog
		ref="dialog"
		v-model="isDialogOpen"
		attach="#data-entry"
		:max-width="width"
		persistent
		:retain-focus="!hasQcCommentsPanel"
		:content-class="contentClass"
		eager
		transition="fade-transition"
		@keydown="dialogKeydownHandler"
		@click:outside="dialogClickHandler"
	>
		<v-card class="metadata-dialog-content-v2 pb-2">
			<v-metadata-header
				v-model:expanded="expanded"
				:is-admin="isAdmin"
				:mode="mode"
				@select-location="handleSelectLocation"
			/>
			<v-container class="px-2 py-0">
				<v-metadata-tag-group-tabs-bar
					v-model:current-tag-group-tab="currentTagGroupTab"
					:tag-group-tabs="tagGroupTabs"
					@update:current-tag-group-tab="handleTabChange"
				>
					<template #tagGroupTab="{ tab }">
						<div class="metadata-rows">
							<v-metadata-row
								v-for="{ originalIndex } in filterMetadataToTagGroup(metadata)"
								:key="originalIndex"
								ref="metadataRows"
								v-model:index-tag-map="indexTagMap"
								v-model:metadata="metadata"
								v-model:metadatum="metadata[originalIndex]"
								v-model:location="location"
								:bounds="bounds"
								:composite-key-utils="compositeKeyUtils"
								:extraction-composite-keys="extractionCompositeKeys"
								:copy-row-result="
									copyRowResults[metadata[originalIndex].index] || []
								"
								:current-tag-group-tab="currentTagGroupTab"
								:direct-text-select="directTextSelect"
								:exclude-keys="excludeKeys"
								:header-type="headerType"
								:index="originalIndex"
								:initial-composite-key="initialCompositeKey"
								:limit-tag-groups="limitTagGroups"
								:mode="mode"
								:read-only="readOnly"
								:select-text-function="selectTextFunction"
								:suggestion-map="suggestionMap"
								:tab="tab"
								:tag-group="tagGroup"
								:tag-group-tabs="tagGroupTabs"
								@add-row="addRow"
								@close="close"
								@copy-row="copyRow(metadata[originalIndex].index)"
								@delete-row="deleteRow(metadata[originalIndex].index)"
								@refresh-height="refreshHeight"
								@validate-values="validateValues"
							/>
						</div>
					</template>
				</v-metadata-tag-group-tabs-bar>
			</v-container>
			<v-metadata-table-footer
				v-model:is-blank-table-cell="isBlankTableCell"
				v-model:mode="mode"
				:axis-type="axisType"
				:composite-key-utils="compositeKeyUtils"
				:current-tag-group-tab="currentTagGroupTab"
				:header-data="headerData"
				:metadata="metadata"
				:read-only="readOnly"
				:tag-group-tabs="tagGroupTabs"
				@update:is-blank-table-cell="handleBlankTableCellUpdate"
			/>
		</v-card>
		<v-metadata-select-overlay type="text" @close="close" />
		<v-metadata-select-overlay type="location" @close="close" />
	</v-dialog>
</template>

<script>
import _ from "lodash";
import { toRaw } from "vue";
import utils from "@/utils";
import createCompositeKeyUtils from "@/compositeKeyUtils";
import { v4 as uuidv4 } from "uuid";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import { useLDFlag } from "launchdarkly-vue-client-sdk";

import VMetadataHeader from "./VMetadataHeader.vue";
import VMetadataTagGroupTabsBar from "./VMetadataTagGroupTabsBar.vue";
import VMetadataRow from "./VMetadataRow.vue";
import VMetadataSelectOverlay from "./VMetadataSelectOverlay.vue";
import VMetadataTableFooter from "./VMetadataTableFooter.vue";

import { Mode } from "./VMetadataConstants.js";
import { reindexAfterRowDelete } from "./metadataRowIndex.js";

const textSelectState = {
	cancellable: null,
	ctx: { bounds: null, container: null, visible: false },
	settings: {
		exitOnClose: false,
	},
	begin(selectTextFn, keydownHandler, ctx, settings) {
		this.ctx = { ...(ctx || {}), visible: true };
		this.settings = { ...this.settings, ...(settings || {}) };
		this.cancellable = new utils.Cancellable((resolve) => {
			selectTextFn(resolve, keydownHandler);
		});
		return this.cancellable;
	},
	finish() {
		this.cancellable = null;
		this.ctx = { ...this.ctx, visible: false };
	},
	cancel() {
		this.cancellable?.cancel?.();
		this.cancellable = null;
		this.ctx = { ...this.ctx, visible: false };
	},
};

const locationSelectState = {
	cancellable: null,
	ctx: { bounds: null, container: null, visible: false },
	begin(selectLocationFn, keydownHandler, ctx) {
		this.ctx = { ...(ctx || {}), visible: true };
		this.cancellable = new utils.Cancellable((resolve) => {
			selectLocationFn(resolve, keydownHandler);
		});
		return this.cancellable;
	},
	finish() {
		this.cancellable = null;
		this.ctx = { ...this.ctx, visible: false };
	},
	cancel() {
		this.cancellable?.cancel?.();
		this.cancellable = null;
		this.ctx = { ...this.ctx, visible: false };
	},
};

const excludeKeys = [
	"uuid",
	"tagGroup",
	"tagGroups",
	"type",
	"compositeKey",
	"referenceCompositeKey",
	"tagGroupAxisDirection",
	"pointGroupAxisDirection",
	"tuple",
];

const createDefaultState = () => ({
	arrow: null,
	arrowPadding: 8,
	arrowSize: 8,
	axisType: null,
	compositeKeyUtils: null,
	copyRowResults: {},
	contentClass: "metadata-dialog-v2",
	currentCompositeKey: null,
	currentTagGroupTab: 0,
	defaultTags: null,
	directTextSelect: false,
	element: null,
	excludeKeys,
	excludeTags: [],
	expanded: true,
	extractionCompositeKeys: [],
	forceAxis: null,
	forceDirection: null,
	headerData: {},
	headerType: null,
	indexTagMap: {},
	initialCompositeKey: null,
	isBlankTableCell: false,
	isDialogOpen: false,
	limitTagGroups: [],
	location: null,
	metadata: [],
	mode: "standard",
	dialogOffset: 0,
	position: null,
	preventClosing: false,
	reject: null,
	resolve: null,
	screenPositionFunction: null,
	selectTextFunction: null,
	selectLocationFunction: null,
	suggestionMap: null,
	tagGroup: null,
	tagGroupTabs: [],
	tagGroupTagMap: {},
	textSelect: textSelectState,
	locationSelect: locationSelectState,
});

export default {
	name: "VMetadataV2",
	components: {
		VMetadataHeader,
		VMetadataTagGroupTabsBar,
		VMetadataRow,
		VMetadataSelectOverlay,
		VMetadataTableFooter,
	},

	provide() {
		return {
			dialogKeydownHandler: this.dialogKeydownHandler,
			getFilteredTags: this.getFilteredTags,
			getValidationRules: this.getValidationRules,
			isKeyTag: this.isKeyTag,
			isReferenceKeyTag: this.isReferenceKeyTag,
			locationSelect: this.locationSelect,
			showKeyPicker: this.showKeyPicker,
			textSelect: this.textSelect,
			validateValues: this.validateValues,
		};
	},

	props: {
		bounds: { type: Object, default: null },
		hasQcCommentsPanel: { type: Boolean, default: false },
		isAdmin: { type: Boolean, default: false },
		largeWidth: { type: Number, default: 550 },
		readOnly: { type: Boolean, default: false },
		smallWidth: { type: Number, default: 300 },
		tagGroups: { type: Array, default: () => [] },
		tags: { type: Array, default: () => [] },
	},

	setup() {
		const pointGroupOverhaul = useLDFlag("point-group-overhaul");
		const undoRedoEnabled = useLDFlag("undo-redo-enabled");
		return {
			pointGroupOverhaul,
			undoRedoEnabled,
		};
	},

	data() {
		return createDefaultState();
	},
	computed: {
		width() {
			return this.expanded ? this.largeWidth : this.smallWidth;
		},
	},

	watch: {
		location(val) {
			if (this.screenPositionFunction && val?.x && val?.y) {
				const position = this.screenPositionFunction(val);
				this.setPosition(position);
			}
		},
	},

	methods: {
		...mapActions(useMainStore, ["setDialogExpanded"]),
		open(args) {
			const {
				axisType,
				currentCompositeKey,
				defaultTags,
				directTextSelect,
				excludeTags,
				extractionCompositeKeys,
				forceAxis,
				forceDirection,
				data,
				headerData,
				headerType,
				isBlankTableCell,
				isBranchEdit,
				isBranchSelection,
				limitTagGroups,
				dialogOffset,
				position,
				selectTextFunction,
				selectLocationFunction,
				suggestionMap,
				tagGroup,
				tagGroupTabs,
				screenPositionFunction,
			} = args;

			if (isBranchEdit) {
				this.mode = Mode.BRANCH_EDIT;
			} else if (isBranchSelection) {
				this.mode = Mode.BRANCH_SELECT;
			} else if (isBlankTableCell) {
				this.mode = Mode.BLANK_TABLE_CELL;
			} else if (axisType === "table") {
				this.mode = Mode.TABLE_CELL;
			} else if (axisType === "tableHeader") {
				this.mode = Mode.TABLE_HEADER;
			} else if (axisType === "barGraphHeader") {
				this.mode = Mode.BAR_GRAPH_HEADER;
			} else {
				this.mode = Mode.STANDARD;
			}

			this.isDialogOpen = true;

			this.compositeKeyUtils = createCompositeKeyUtils(this.tags);
			this.axisType = axisType;
			this.isBlankTableCell = isBlankTableCell;
			this.extractionCompositeKeys = extractionCompositeKeys;
			this.currentCompositeKey = currentCompositeKey;
			this.defaultTags = defaultTags;
			this.directTextSelect = directTextSelect;
			this.excludeTags = excludeTags ?? [];
			this.forceAxis = forceAxis;
			this.forceDirection = forceDirection;
			this.headerData = headerData;
			this.headerType = headerType;
			this.limitTagGroups = limitTagGroups;
			this.dialogOffset = dialogOffset ?? 0;
			this.selectTextFunction = selectTextFunction;
			this.selectLocationFunction = selectLocationFunction;
			this.suggestionMap = suggestionMap;
			this.tagGroup = tagGroup;
			this.screenPositionFunction = screenPositionFunction;

			this.element = this.$refs.dialog?.contentEl || null;

			if (!this.arrow) {
				this.arrow = document.createElement("div");
				this.element.parentElement.prepend(this.arrow);
			}

			this.resetPosition();

			this.setAxisPolicy();
			this.tagGroupTabs = tagGroupTabs ?? [];
			this.populateTagData();
			this.populateMetadata(data);
			this.setDefaultTab();

			if (position) {
				this.setPosition(position);
			}

			if (this.directTextSelect && this.selectTextFunction) {
				this.$nextTick(() => {
					const firstRow = this.$refs.metadataRows.find(
						(row) => row.metadatum.index === 0,
					);
					if (firstRow.$refs.valueField) {
						firstRow.$refs.valueField.$refs.textSelectIcon.$el.click();
					}
				});
			}

			return new Promise((resolve, reject) => {
				this.resolve = resolve;
				this.reject = reject;
			});
		},
		close() {
			this.isDialogOpen = false;

			this.$nextTick(function () {
				// hide arrow
				if (this.arrow) {
					this.arrow.className = "";
				}

				if (this.resolve) {
					this.resolve(this.exportMetadata());
					this.clear();

					if (this.undoRedoEnabled) {
						window.postMessage("keydown");
					}
				}
			});
		},
		clear() {
			Object.assign(this.$data, createDefaultState());
		},
		setAxisPolicy() {
			switch (this.axisType) {
				case "tableHeader":
					this.arrowPadding = 0;
					this.emptyOnExit = true;
					break;
				case "barGraphHeader":
					this.arrowPadding = 0;
					this.emptyOnExit = true;
					break;
				case "table":
					this.arrowPadding = 0;
					break;
				case "image":
					this.emptyOnExit = true;
					break;
				case "bar":
				case "map":
				case "polar":
				case "ternary":
				case "xy":
			}
		},
		setPosition: function (position) {
			this.position = position;
			this.refreshHeight();
			this.refreshPosition(this.position);
		},
		resetPosition: function () {
			this.element.style.top = "auto";
			this.element.style.right = "auto";
			this.element.style.bottom = "auto";
			this.element.style.left = "auto";

			this.arrow.style.top = "auto";
			this.arrow.style.right = "auto";
			this.arrow.style.bottom = "auto";
			this.arrow.style.left = "auto";

			this.position = null;
		},
		refreshHeight: function () {
			this.$nextTick(function () {
				this.height = this.element.getBoundingClientRect().height;
			});
		},
		refreshPosition: function (position) {
			this.$nextTick(function () {
				// translate x and y values to viewport pixels
				const x = position.x + this.bounds.left;
				const y = position.y + this.bounds.top;

				// get viewport height to calculate bottom
				const viewportBounds =
					this.element.parentElement.getBoundingClientRect();
				const viewportMaxY = viewportBounds.height;
				const viewportMaxX = viewportBounds.width;

				const direction = this.forceDirection
					? this.flipDirectionToFitViewport(this.forceDirection, x, y)
					: this.determineDirection(x, y);

				switch (direction) {
					case "north":
						this.element.style.bottom = `${
							viewportMaxY -
							y +
							this.arrowPadding +
							this.arrowSize +
							this.dialogOffset
						}px`;
						this.element.style.left = `${x - this.width / 2}px`;

						this.arrow.style.bottom = `${
							viewportMaxY - y + this.arrowPadding + this.dialogOffset
						}px`;
						this.arrow.style.left = `${x - this.arrowSize}px`;

						this.arrow.className = "arrow-down";

						break;
					case "east":
						this.element.style.top = `${y - this.height / 2}px`;
						this.element.style.left = `${
							x + this.arrowPadding + this.arrowSize + this.dialogOffset
						}px`;

						this.arrow.style.top = `${y - this.arrowSize}px`;
						this.arrow.style.left = `${
							x + this.arrowPadding + this.dialogOffset
						}px`;

						this.arrow.className = "arrow-left";

						break;
					case "south":
						this.element.style.top = `${
							y + this.arrowPadding + this.arrowSize + this.dialogOffset
						}px`;
						this.element.style.left = `${x - this.width / 2}px`;

						this.arrow.style.top = `${
							y + this.arrowPadding + this.dialogOffset
						}px`;
						this.arrow.style.left = `${x - this.arrowSize}px`;

						this.arrow.className = "arrow-up";

						break;
					case "west":
						this.element.style.top = `${y - this.height / 2}px`;
						this.element.style.right = `${
							viewportMaxX -
							x +
							this.arrowPadding +
							this.arrowSize +
							this.dialogOffset
						}px`;

						this.arrow.style.top = `${y - this.arrowSize}px`;
						this.arrow.style.right = `${
							viewportMaxX - x + this.arrowPadding + this.dialogOffset
						}px`;

						this.arrow.className = "arrow-right";

						break;
				}
			});
		},
		determineDirection: function (x, y) {
			let direction = "";

			switch (this.forceAxis) {
				case "x":
					direction = "east";
					if (this.bounds.right - x < this.width) {
						direction = "west";
					}
					break;
				case "y":
					direction = "south";
					if (this.bounds.bottom - y < this.height) {
						direction = "north";
					}
					break;
				default:
					direction = "south";
					if (this.bounds.bottom - y < this.height) {
						direction = "north";
					}
					if (x - this.bounds.left < this.width / 2) {
						direction = "east";
					} else if (this.bounds.right - x < this.width / 2) {
						direction = "west";
					}
					break;
			}

			return direction;
		},
		// Header dialogs (bar headers, table row/col headers) pass an explicit
		// `forceDirection` derived from the bar growth direction / header
		// orientation, so the dialog opens on the side away from the data being
		// annotated. That preference ignores the viewport edge, so a header near
		// the left edge opens "west" and the dialog body spills off-screen. Keep
		// the forced side when the dialog fits there, but flip to the opposite
		// side when it would clip - mirroring the edge-aware behavior the
		// non-forced `determineDirection` path already applies.
		flipDirectionToFitViewport: function (direction, x, y) {
			if (!this.bounds) {
				return direction;
			}
			const width = this.width || 0;
			const height = this.height || 0;
			switch (direction) {
				case "west":
					return x - this.bounds.left < width ? "east" : "west";
				case "east":
					return this.bounds.right - x < width ? "west" : "east";
				case "north":
					return y - this.bounds.top < height ? "south" : "north";
				case "south":
					return this.bounds.bottom - y < height ? "north" : "south";
				default:
					return direction;
			}
		},

		dialogKeydownHandler(event) {
			switch (event.key) {
				case "Enter":
					if (this.readOnly) {
						this.close();
						return;
					}

					// add a new row without closing the dialog
					if (event.shiftKey) {
						// append new row
						if (
							![
								Mode.BLANK_TABLE_CELL,
								Mode.BRANCH_EDIT,
								Mode.BRANCH_SELECT,
							].includes(this.mode)
						)
							this.addRow();
					} else {
						const isValid = this.validateValues();
						if (isValid) {
							this.textSelect?.cancel();
							this.locationSelect?.cancel();
							this.close();
						}

						if (this.mode === Mode.TABLE_CELL) {
							document.dispatchEvent(new Event("next-cell"));
						}
					}

					// stop from bubbling to dactyl shortcuts
					event.stopPropagation();

					break;
				case "Backspace":
					// disable default WPD backspace behavior (deletes point)
					event.stopPropagation();
					// delete a row and prevent deleting reindexed values
					if (event.shiftKey && this.mode !== Mode.BLANK_TABLE_CELL) {
						// remove last row
						const index =
							this.element.getElementsByClassName("dactyl-metadata-row")
								.length - 1;

						if (index > 1) {
							this.deleteRow(index);
							event.preventDefault();
						}
					}
					break;
				case "Escape":
					if (this.readOnly) {
						this.close();
					} else if (this.textSelect) {
						this.textSelect.cancel();

						if (this.textSelect?.settings?.exitOnClose) {
							this.close();
						}
					} else if (this.locationSelect) {
						this.locationSelect.cancel();
					} else if (!this.preventClosing) {
						this.close();
					}

					event.stopPropagation();

					break;
				case "ArrowLeft":
					// collapse the dialog
					// TODO: events with ctrl held aren't being correctly detected
					if (event.ctrlKey) {
						event.stopPropagation();
						this.expanded = false;
						this.setDialogExpanded(false);
					}

					if (this.mode === Mode.TABLE_CELL) {
						event.stopPropagation();
					}
					break;
				case "ArrowRight":
					// expand the dialog
					// TODO: events with ctrl held aren't being correctly detected
					if (event.ctrlKey) {
						event.stopPropagation();
						this.expanded = true;
						this.setDialogExpanded(true);
					}

					if (this.mode === Mode.TABLE_CELL) {
						event.stopPropagation();
					}
					break;
				case "ArrowUp":
				case "ArrowDown":
					if (this.mode === Mode.TABLE_CELL) {
						event.stopPropagation();
					}
					break;
				default:
					// stop from bubbling to dactyl shortcuts
					event.stopPropagation();
					break;
			}
		},

		async handleSelectLocation(event) {
			if (!this.selectLocationFunction) {
				console.warn("selectLocationFunction not available");
				return;
			}

			const container = event.target.closest?.(".v-overlay-container");
			if (!container) {
				console.warn("container not found");
				return;
			}

			let selectLocationTool;
			const cancellable = this.locationSelect.begin(
				(resolve, keydownHandler) => {
					if (this.selectLocationFunction) {
						selectLocationTool = this.selectLocationFunction(
							resolve,
							keydownHandler,
						);
					}
				},
				this.dialogKeydownHandler,
				{ bounds: this.bounds, container: container },
			);

			try {
				const { location } = await cancellable.promise;

				if (location) {
					this.location = location;
				}

				if (selectLocationTool?.detach) selectLocationTool.detach();
			} catch (error) {
				if (selectLocationTool?.detach) selectLocationTool.detach();
			}

			// set focus back on value input of the row
			event.target
				.closest(".metadata-dialog-content-v2")
				?.querySelector(".metadata-value-field input[type='text']")
				?.focus();

			// clear the cancellable
			this.locationSelect.finish();
		},

		dialogClickHandler(event) {
			// Allow interaction with the QC comments panel
			const clickedPanel = event.target?.closest?.(".qc-comments-panel");
			if (clickedPanel) return;

			if (this.readOnly) {
				this.close();
				return;
			}

			const isValid = this.validateValues();

			if (!isValid || this.preventClosing) {
				const metadataDialog = document.querySelector(".metadata-dialog");
				if (metadataDialog) {
					metadataDialog.focus();
				}
			} else {
				this.close();
			}

			event.stopImmediatePropagation();
		},

		setDefaultTab() {
			if (this.tagGroupTabs && this.tagGroupTabs.length > 1) {
				const findTabWithData = (dataSource) => {
					return this.tagGroupTabs.findIndex((tab) =>
						dataSource.some((item) => item.tagGroup?.uuid === tab.uuid),
					);
				};

				const metadataTabIndex = findTabWithData(
					this.metadata.filter((md) => md.tag),
				);
				const headerTabIndex = findTabWithData(
					[
						...(this.headerData?.rowHeaderData || []),
						...(this.headerData?.colHeaderData || []),
					].filter((hd) => !hd.isCompositeKey),
				);

				const tabIndex =
					metadataTabIndex >= 0 ? metadataTabIndex : headerTabIndex;

				if (tabIndex >= 0) {
					this.handleTabChange(tabIndex);
				}
			}
		},
		populateTagData() {
			const limitUuids = new Set(
				(this.limitTagGroups || []).map((g) => g.uuid),
			);
			const groups =
				limitUuids.size === 0
					? this.tagGroups
					: this.tagGroups.filter((g) => limitUuids.has(g.uuid));

			const showAllTags =
				this.axisType === "tableHeader" || this.mode === Mode.BRANCH_EDIT;

			const tagsWithGroup = groups
				.flatMap((g) => g.tags.map((tag) => ({ ...tag, tagGroupUuid: g.uuid })))
				.sort((a, b) => a.name.localeCompare(b.name));

			const visibleByGroup = showAllTags
				? tagsWithGroup
				: tagsWithGroup.filter((tag) => !this.isKeyTag(tag));

			this.tagGroupTagMap = _.groupBy(visibleByGroup, "tagGroupUuid");

			const uniqueAllTags = _.uniqBy(
				groups
					.flatMap((g) => g.tags)
					.sort((a, b) => a.name.localeCompare(b.name)),
				"uuid",
			);

			this.filteredTags = showAllTags
				? uniqueAllTags
				: uniqueAllTags.filter((tag) => !this.isKeyTag(tag));
		},

		populateMetadata(rawData) {
			// make sure uuid is the first element
			// add a uuid if it doesn't already exist
			this.metadata.push(
				{
					index: "uuid",
					tag: null,
					value: rawData?.uuid || uuidv4(),
				},
				{
					index: "tagGroup",
					tag: null,
					value: this.tagGroup,
				},
				{
					index: "tagGroups",
					tag: null,
					value: rawData?.tagGroups,
				},
				{
					index: "type",
					tag: null,
					value: this.axisType,
				},
				{
					index: "compositeKey",
					tag: null,
					value: rawData?.compositeKey,
				},
				{
					index: "referenceCompositeKey",
					tag: null,
					value: rawData?.referenceCompositeKey,
				},
				...(!this.pointGroupOverhaul
					? [
							{
								index: "tagGroupAxisDirection",
								tag: null,
								value: rawData?.tagGroupAxisDirection,
							},
					  ]
					: []),
				...(this.pointGroupOverhaul
					? [
							{
								index: "pointGroupAxisDirection",
								tag: null,
								value: rawData?.pointGroupAxisDirection,
							},
					  ]
					: []),
				...(this.pointGroupOverhaul
					? [
							{
								index: "tuple",
								tag: null,
								value: rawData?.tuple,
							},
					  ]
					: []),
			);

			if (_.isEmpty(rawData)) {
				const filteredTagUuids = this.getFilteredTags(0).map((tag) => tag.uuid);

				const filteredDefaultTags = this.defaultTags
					? this.defaultTags.filter((tag) => {
							return filteredTagUuids.includes(tag.uuid);
					  })
					: this.defaultTags;
				if (filteredDefaultTags && filteredDefaultTags.length > 0) {
					// default tag group tags
					filteredDefaultTags.forEach(({ uuid, tagGroup }, index) => {
						const tag = this.findTag(uuid);
						this.metadata.push({
							index: index,
							tag: tag,
							value: "",
							tagGroup,
						});

						this.indexTagMap[index] = tag;
					});
				} else {
					this.metadata.push({
						index: 0,
						tag: null,
						value: "",
						...(!_.isEmpty(this.tagGroupTabs)
							? {
									tagGroup: this.tagGroupTabs[this.currentTagGroupTab],
							  }
							: {}),
					});
				}
			} else {
				// set label if necessary
				if (rawData.label) {
					if (_.isObject(rawData.label)) {
						this.metadata.push({
							index: "label",
							tag: rawData.label.tag,
							value: rawData.label.value,
						});
					} else {
						this.metadata.push({
							index: "label",
							tag: null,
							value: rawData.label,
						});
					}
				}

				_.forEach(
					_.omit(rawData, [...this.excludeKeys, "label"]),
					(data, index) => {
						const value =
							data.isCompositeKey && !data.hidden
								? rawData.compositeKey
								: data.value ?? "";

						const tag = data.tag ?? this.findTag(data.tagUUID);
						this.metadata.push({
							index: index,
							tag,
							value,
							axis: data.axis ?? undefined,
							axisType: data.axisType ?? undefined,
							hidden: data.hidden,
							applyToAllHeaders: data.applyToAllHeaders,
							disableApplyToAllHeaders: !!data.disableApplyToAllHeaders,
							headerTag: data.headerTag,
							tagGroup: data.tagGroup,
							fromBroadcast: data.fromBroadcast,
							broadcastValue: data.broadcastValue,
							datasetUuid: data.datasetUuid,
						});

						this.indexTagMap[index] = tag;
					},
				);

				if (this.mode === Mode.BRANCH_EDIT) {
					this.initialCompositeKey = this.metadata.find(
						(metadatum) => metadatum.index === "compositeKey",
					)?.value;
				}

				// Per-tab carryover for cells that already have data in some
				// tabs but not others. Tabs with their own rows are left
				// alone; empty tabs receive the carried-over tags scoped to
				// that tab's tag group. Single-tag-group tables are
				// unaffected (one tab cannot be partially filled).
				if (
					this.tagGroupTabs?.length > 1 &&
					this.defaultTags &&
					this.defaultTags.length > 0
				) {
					this.tagGroupTabs.forEach((tab, tabIndex) => {
						const tabHasRows = this.metadata.some(
							(md) =>
								!isNaN(md.index) &&
								!md.hidden &&
								md.tagGroup?.uuid === tab.uuid,
						);
						if (tabHasRows) return;

						const tabTagUuids = new Set(
							this.getFilteredTags(0, tabIndex).map((t) => t.uuid),
						);
						const carryover = this.defaultTags.filter(
							(dt) =>
								tabTagUuids.has(dt.uuid) &&
								(!dt.tagGroup || dt.tagGroup.uuid === tab.uuid),
						);
						if (carryover.length === 0) return;

						let nextIndex = this.getMaxIndex();
						carryover.forEach(({ uuid }) => {
							nextIndex = nextIndex < 0 ? 0 : nextIndex + 1;
							const tag = this.findTag(uuid);
							this.metadata.push({
								index: nextIndex,
								tag,
								value: "",
								tagGroup: tab,
							});
							this.indexTagMap[nextIndex] = tag;
						});
					});
				}
			}
		},

		exportMetadata() {
			const result = {};

			// if this is a table cell and it is marked as blank, return immediately
			if (this.mode === Mode.BLANK_TABLE_CELL) {
				return {
					blank: true,
				};
			}

			// if empty is allowed and should be empty on exit and nothing has been added,
			// remove uuid
			if (this.emptyOnExit && this.isClean()) {
				this.metadata = [];
			}

			if (this.mode === Mode.BRANCH_SELECT) {
				const compositeKeyData = this.metadata.find(
					(metadatum) =>
						!isNaN(metadatum.index) && metadatum.value && !metadatum.tag,
				);

				return compositeKeyData
					? { compositeKey: compositeKeyData.value }
					: null;
			}

			// if there is no valid non-empty metadata row, return empty object
			const hasValidMetadataRow = this.metadata.some(
				(md) =>
					!this.excludeKeys.includes(md.index) &&
					!_.isNil(md.value) &&
					(!_.isString(md.value) || _.trim(md.value) !== "") &&
					!_.isEmpty(md.tag),
			);
			if (!hasValidMetadataRow) {
				return result;
			}

			let hasReference = false;
			_.forEach(this.metadata, (metadatum) => {
				// omit empty metadata fields
				if (metadatum.value !== "" && metadatum.value !== undefined) {
					if (this.excludeKeys.includes(metadatum.index)) {
						switch (typeof metadatum.value) {
							case "string":
								result[metadatum.index] = [
									"compositeKey",
									"referenceCompositeKey",
								].includes(metadatum.index)
									? metadatum.value
									: metadatum.value.trim();
								break;
							case "object":
								result[metadatum.index] = metadatum.value;
								break;
						}
					} else if (!_.isEmpty(metadatum.tag)) {
						// Preserve fromBroadcast/broadcastValue only when the saved
						// value still matches the broadcast value at stamp time.
						// Curator-edited values lose the flag so cascade-removal of
						// the broadcast tag leaves them alone. broadcastValue is the
						// raw string from the calibration input, while metadatum.value
						// may have been parseFloat-coerced on a prior save - so allow
						// numeric equivalence ("5" matches 5) before falling back to
						// strict equality.
						const valuesEqual = (a, b) => {
							if (a === b) return true;
							if (utils.isNumber(a) && utils.isNumber(b)) {
								return parseFloat(a) === parseFloat(b);
							}
							return false;
						};
						const broadcastMatches =
							metadatum.fromBroadcast &&
							valuesEqual(metadatum.value, metadatum.broadcastValue);

						// make sure numbers are represented as plain numbers
						// javascript converts number less than 10^-6 and
						// greater than 10^21 to scientific notation
						if (utils.isNumber(metadatum.value)) {
							// do not cast study branch names to numbers
							if (
								!this.compositeKeyUtils.branchTags.includes(metadatum.tag.name)
							) {
								// cast string values to number
								metadatum.value = parseFloat(metadatum.value);
							}
						} else if (!_.isEmpty(metadatum.value)) {
							// trim string values
							metadatum.value = this.showKeyPicker(metadatum?.tag)
								? metadatum.value
								: metadatum.value.trim();
						}

						result[metadatum.index] = {
							tag: _.pick(metadatum.tag, ["name", "uuid"]),
							value: metadatum.value,
							hidden: metadatum.hidden,
							headerTag: metadatum.headerTag,
							...(this.mode === Mode.TABLE_HEADER ||
							this.mode === Mode.BAR_GRAPH_HEADER
								? {
										applyToAllHeaders: !!metadatum.applyToAllHeaders,
										disableApplyToAllHeaders:
											!!metadatum.disableApplyToAllHeaders,
								  }
								: {}),
							...(metadatum.axis ? { axis: metadatum.axis } : {}),
							...(metadatum.axisType ? { axisType: metadatum.axisType } : {}),
							...(metadatum.tagGroup ? { tagGroup: metadatum.tagGroup } : {}),
							...(broadcastMatches
								? {
										fromBroadcast: true,
										broadcastValue: metadatum.broadcastValue,
										...(metadatum.datasetUuid
											? { datasetUuid: metadatum.datasetUuid }
											: {}),
								  }
								: {}),
						};

						if (
							metadatum.tag &&
							this.compositeKeyUtils.isReferenceBranchTag(metadatum.tag.name)
						) {
							hasReference = true;
							result.referenceCompositeKey = metadatum.value;
						}
					}
				}
			});

			// check if reference composite key needs to be removed
			if (result.referenceCompositeKey) {
				if (!hasReference) {
					delete result.referenceCompositeKey;
				}
			}

			if (this.location !== null) {
				result.location = structuredClone(toRaw(this.location));
			}

			return result;
		},

		handleBlankTableCellUpdate(value) {
			this.isBlankTableCell = value;
			this.mode = value ? Mode.BLANK_TABLE_CELL : Mode.STANDARD;
		},

		handleTabChange(newTabIndex) {
			if (this.tagGroupTabs.length <= 1) {
				return;
			}

			this.currentTagGroupTab = newTabIndex;
			const currentTab = this.tagGroupTabs[newTabIndex];
			const newTabFilteredMetadata = this.filterMetadataToTagGroup(
				this.metadata,
			);

			const hasMetadata = newTabFilteredMetadata.some((metadatum) => {
				return (
					!isNaN(metadatum.index) &&
					!metadatum.hidden &&
					metadatum.tagGroup?.uuid === currentTab.uuid
				);
			});

			if (!hasMetadata) {
				this.addRow(currentTab);
			}
		},

		addRow: function () {
			// Only stamp per-entry tagGroup in multi-tab mode (tables).
			// For single tag group data points it's redundant and causes
			// stale references if the dataset's tag group is later changed.
			const tagGroup = !_.isEmpty(this.tagGroupTabs)
				? this.tagGroupTabs[this.currentTagGroupTab]
				: null;

			const maxIndex = this.getMaxIndex();
			this.metadata.push({
				index: maxIndex < 0 ? 0 : maxIndex + 1,
				tag: null,
				value: "",
				...(tagGroup ? { tagGroup } : {}),
			});

			this.refreshHeight();
			this.validateValues();
		},

		copyRow(index) {
			const currentTabUuid = this.tagGroupTabs[this.currentTagGroupTab]?.uuid;
			if (!currentTabUuid) return;

			const copiedMetadatum = this.metadata.find((md) => md.index === index);
			if (!copiedMetadatum || !copiedMetadatum.tag?.uuid) return;

			let nextIndex = this.getMaxIndex() + 1;

			const existingTagUuidsByTab = this.metadata
				.filter((md) => !isNaN(md.index) && !md.hidden && md.tagGroup?.uuid)
				.reduce((acc, md) => {
					const tabUuid = md.tagGroup.uuid;
					(acc[tabUuid] ||= new Set()).add(md.tag?.uuid);
					return acc;
				}, {});

			const results = [];
			for (const [tabIndex, tab] of this.tagGroupTabs.entries()) {
				if (tab.uuid === currentTabUuid) continue;

				const tagGroupTags = this.getFilteredTags(nextIndex, tabIndex);
				const alreadyHasTag = existingTagUuidsByTab[tab.uuid]?.has?.(
					copiedMetadatum.tag?.uuid,
				);

				const isValidTag = tagGroupTags.find(
					(tag) => tag.uuid === copiedMetadatum.tag?.uuid,
				);

				const emptyTabMetadata = this.metadata.find(
					(md) =>
						!isNaN(md.index) &&
						!md.hidden &&
						md.tagGroup?.uuid === tab.uuid &&
						!md.value &&
						(!md.tag?.uuid || md.tag?.uuid === copiedMetadatum.tag?.uuid),
				);

				if (alreadyHasTag || !isValidTag) {
					results.push({
						copiedTab: null,
						skippedTab: tab,
						message: alreadyHasTag
							? "Tag already exists"
							: `"${copiedMetadatum.tag?.name}" is not a valid "${tab.name}" tag`,
					});
				} else if (emptyTabMetadata) {
					emptyTabMetadata.value = copiedMetadatum.value;
					emptyTabMetadata.tag = copiedMetadatum.tag;
					results.push({ copiedTab: tab, skippedTab: null });
				} else if (!alreadyHasTag) {
					this.metadata.push({
						...copiedMetadatum,
						index: nextIndex++,
						tagGroup: tab,
					});
					results.push({ copiedTab: tab, skippedTab: null });
				}
			}

			this.copyRowResults = { ...this.copyRowResults, [index]: results };
		},

		deleteRow(index) {
			if (this.mode === Mode.BRANCH_EDIT) {
				this.metadata = [];
				this.close();
				return;
			}

			const mIndex = this.metadata.findIndex((md) => md.index === index);
			if (!mIndex) {
				return;
			}

			const removedTag = this.metadata.splice(mIndex, 1)[0];

			let hasData = true;
			if (!_.isEmpty(this.tagGroupTabs)) {
				const tabCounts = this.metadata
					.filter((md) => !md.hidden && (md.tag || md.value))
					.reduce((counts, md) => {
						if (md.tagGroup && md.tagGroup.uuid) {
							counts[md.tagGroup.uuid] = (counts[md.tagGroup.uuid] || 0) + 1;
						}
						return counts;
					}, {});

				hasData = Object.values(tabCounts).some((count) => count > 0);
			} else {
				hasData = this.metadata.some(
					(md) => !md.hidden && !this.excludeKeys.includes(md.index),
				);
			}

			// only uuid and tagGroup remain, clear and close dialog
			if (!hasData) {
				this.metadata = [];
				this.close();
			} else {
				this.refreshHeight();
				this.validateValues();

				// re-index untagged values
				reindexAfterRowDelete(this.metadata, removedTag.index);

				// if removing the last row from a tag group, add an empty row
				if (!_.isEmpty(this.tagGroupTabs) && removedTag.tagGroup) {
					const remainingTagGroupRows = this.metadata.filter(
						(md) =>
							!md.hidden && md.tagGroup?.uuid === removedTag.tagGroup?.uuid,
					);
					if (remainingTagGroupRows.length === 0) {
						this.addRow(removedTag.tagGroup);
					}
				}
			}
		},

		getMaxIndex() {
			return this.metadata
				.map((item) => item.index)
				.filter((index) => !isNaN(index))
				.map((index) => parseInt(index, 10))
				.reduce((max, current) => Math.max(max, current), -Infinity);
		},

		validateValues() {
			let allValid = true;
			this.metadata.forEach((metadatum) => {
				const rules = this.getValidationRules(metadatum.index);
				rules.forEach((rule) => {
					const result = rule(metadatum.value);
					if (result !== true) allValid = false;
				});
			});

			this.preventClosing = !allValid;

			return allValid;
		},

		getValidationRules(index) {
			const metadatum = this.metadata.find((md) => md.index === index);

			if (!metadatum) {
				return [];
			}

			switch (this.mode) {
				case Mode.BRANCH_EDIT:
					return this.getBranchEditValidationRules(metadatum);
				case Mode.BAR_GRAPH_HEADER:
					return this.getBarHeaderValidationRules(metadatum);
				default:
					return [];
			}
		},

		getBarHeaderValidationRules(metadatum) {
			if (!metadatum) {
				return [];
			}

			return [
				// ensure the bar header value does not contain the composite key delimiter
				(value) => {
					const delim = this.compositeKeyUtils.compositeKeyDelimiter;
					if (
						value &&
						String(value).includes(delim) &&
						metadatum?.tag &&
						!this.isReferenceKeyTag(metadatum?.tag) &&
						!this.isKeyTag(metadatum?.tag)
					) {
						return `Bar header value cannot contain the characters '${delim}'`;
					}
					return true;
				},
			];
		},

		getBranchEditValidationRules(metadatum) {
			if (!metadatum || !this.isKeyTag(metadatum.tag)) {
				return [];
			}

			const posByTag = {
				[this.compositeKeyUtils.studyTag]: 0,
				[this.compositeKeyUtils.studyPartTag]: 1,
				[this.compositeKeyUtils.studyArmTag]: 2,
				[this.compositeKeyUtils.populationTag]: 3,
				[this.compositeKeyUtils.subgroupTag]: 4,
			};
			const pos = posByTag[metadatum.tag?.name];
			if (pos === undefined) {
				return [];
			}

			const buildDuplicateError = (m) => {
				if (m.subgroup) {
					return `Subgroup '${m.subgroup}' already exists under ${
						m.population ? `population '${m.population}', ` : ""
					} study arm '${m.studyArm}', ${
						m.studyPart ? `study part '${m.studyPart}'` : ""
					}, and study '${m.study}'`;
				}
				if (m.population) {
					return `Population '${
						m.population
					}' already exists under study arm '${m.studyArm}', ${
						m.studyPart ? `study part '${m.studyPart}'` : ""
					}, and study '${m.study}'`;
				}
				if (m.studyArm) {
					return `Study arm '${m.studyArm}' already exists under ${
						m.studyPart ? `study part '${m.studyPart}' and` : ""
					} 'study '${m.study}'`;
				}
				if (m.studyPart) {
					return `Study part '${m.studyPart}' already exists under study '${m.study}'`;
				}
				return `Study '${m.study}' already exists`;
			};

			return [
				(value) => {
					const delim = this.compositeKeyUtils.compositeKeyDelimiter;
					if (value && value.includes(delim)) {
						return `Study branch key cannot contain the characters '${delim}'`;
					}

					const parts = this.compositeKeyUtils.splitCompositeKey(
						this.currentCompositeKey,
					);
					const previousValue = parts[pos];
					if (previousValue && !value) return false;

					parts[pos] = value;
					const nextKey = this.compositeKeyUtils.joinCompositeKey(parts);
					if (nextKey === this.currentCompositeKey) return true;

					const match = this.extractionCompositeKeys.find(
						(k) => k.key === nextKey,
					);
					return match ? buildDuplicateError(match) : true;
				},
			];
		},

		isClean() {
			return _.every(
				this.metadata,
				(md) => isNaN(md.index) || _.isNil(md.value) || md.value === "",
			);
		},

		filterMetadataToTagGroup(metadata) {
			return metadata.reduce((acc, metadatum, originalIndex) => {
				const include = (() => {
					if (isNaN(metadatum.index) || metadatum.hidden) return true;
					const tagGroupUuid = !_.isEmpty(this.tagGroupTabs)
						? this.tagGroupTabs[this.currentTagGroupTab]?.uuid
						: this.tagGroup?.uuid;
					if (!metadatum.tagGroup) return true;

					const isBranchTag = this.compositeKeyUtils.isBranchTag(
						metadatum.tag?.name || "",
					);
					return isBranchTag || metadatum.tagGroup.uuid === tagGroupUuid;
				})();

				if (include) acc.push({ ...metadatum, originalIndex });
				return acc;
			}, []);
		},
		findTag: function (uuid) {
			return this.tags.find((tag) => tag.uuid === uuid);
		},
		getFilteredTags: function (mdIndex, tabIndex) {
			// `keyIndex` stays cross-tab: a cell has at most one branch
			// tag regardless of which tag group tab you're viewing
			// (filterMetadataToTagGroup shares branch tags across tabs by
			// design). Allowing a second branch tag in another tab would
			// contradict that.
			const keyIndex = this.metadata.findIndex(
				(data) => this.isKeyTag(data.tag) && !data.hidden,
			);

			// `referenceKeyIndex` is scoped to the current tab. Reference
			// branch tags are per-tag-group (each tab represents a
			// distinct record at the cell, e.g. baseline vs. outcomes,
			// and each can legitimately reference a different study
			// branch). Without this scoping, adding a reference-branch
			// tag in any tab hides the option from every other tab --
			// the original filter computed this against the full
			// metadata, which was correct for single-TG dialogs but
			// over-restricts the multi-TG table-cell case.
			const tabTagGroupUuid =
				!_.isNil(tabIndex) && !_.isEmpty(this.tagGroupTabs)
					? this.tagGroupTabs[tabIndex]?.uuid
					: this.tagGroup?.uuid;
			const isInCurrentTab = (data) => {
				if (!data.tagGroup) return true; // unscoped legacy entries appear in all tabs
				return data.tagGroup.uuid === tabTagGroupUuid;
			};
			const referenceKeyIndex = this.metadata.findIndex(
				(data) =>
					this.isReferenceKeyTag(data.tag) &&
					!data.hidden &&
					isInCurrentTab(data),
			);

			const tagList =
				!_.isNil(tabIndex) && !_.isEmpty(this.tagGroupTabs)
					? this.tagGroupTagMap[this.tagGroupTabs[tabIndex].uuid]
					: this.filteredTags;

			return tagList.filter((tag) => {
				if (this.excludeTags.includes(tag.name)) return false;

				const isReferenceKey = this.isReferenceKeyTag(tag);
				const isKey = this.isKeyTag(tag);
				if (!isReferenceKey && !isKey) return true;

				if (keyIndex < 0 && referenceKeyIndex < 0) return true;
				if (keyIndex === mdIndex) {
					return referenceKeyIndex >= 0 ? !isReferenceKey : true;
				}

				if (referenceKeyIndex === mdIndex) {
					return keyIndex >= 0 ? !isKey : true;
				}

				if (referenceKeyIndex >= 0 && keyIndex >= 0) {
					return !isKey && !isReferenceKey;
				} else if (referenceKeyIndex >= 0) {
					return !isReferenceKey;
				} else if (keyIndex >= 0) {
					return !isKey;
				}

				return true;
			});
		},

		showKeyPicker(tag) {
			return (
				this.mode === Mode.BRANCH_SELECT ||
				(this.mode !== Mode.BRANCH_EDIT &&
					(this.isKeyTag(tag) || this.isReferenceKeyTag(tag)))
			);
		},
		isKeyTag(tag) {
			return this.compositeKeyUtils.isBranchTag(tag?.name);
		},
		isReferenceKeyTag(tag) {
			return this.compositeKeyUtils.isReferenceBranchTag(tag?.name);
		},
	},
};
</script>

<style lang="scss">
.metadata-dialog-v2 {
	margin: 0 !important;
}

.metadata-dialog-content-v2 {
	border: thin solid $color-dactyl-orange !important;
}

.metadata-rows {
	display: flex;
	flex-direction: column;
	row-gap: 6px;
}
</style>
